import { Link, useLocation } from 'react-router-dom';
import { hourOutcome, lessonToOpen, stillComing, type CoachHour } from '../api/coach-hours.js';
import type { CoachLesson } from '../api/use-coach-day.js';
import type { EmptyClinic } from '../api/use-coach-lesson-hours.js';

/**
 * One hour of a coach's work, as a row (canvas row 8, approved 16 October;
 * moved here from the one-day view it replaced).
 *
 * When, and who is coming; what was sold, the cage and the place; a line for
 * each cancellation; the closure's own label (closureLabel: Completed, No
 * show, Cancelled, Comp, Goodwill, Closed), and nothing while it is open.
 *
 * The day travels with the link, because the lesson screen finds the lesson in
 * that day's read.
 */
export function CoachHourRow({ hour }: { hour: CoachHour }) {
  const lesson = lessonToOpen(hour);
  const outcome = hourOutcome(hour);
  const coming = stillComing(hour);
  // Everyone cancelled: the names are who was booked. Otherwise who is coming.
  const named = coming.length > 0 ? coming : hour.lessons;
  const who = named.map((each) => each.athlete.name).join(' and ');
  const cancelled = hour.lessons.filter((each) => each.cancellation !== null);
  const free = lesson.places_free ?? 0;
  const placesOnSale =
    outcome === null && coming.length > 0 && free > 0 && new Date(lesson.slot_start) > new Date();
  // The colour comes from the closure itself, never from the word on screen.
  const outcomeClass = outcome?.creditUsed ? 'completed' : 'cancelled';
  const on = isoDay(new Date(lesson.slot_start));
  const { pathname, search } = useLocation();

  // A clinic is its own page, with everyone on it to check in (board 5).
  if (lesson.clinic_id) {
    return (
      <Link className="hour-row" to={`/coach/clinics/${lesson.clinic_id}`}>
        <span className="hour-row-text">
          <span className="name">
            {dayAndTime(lesson.slot_start)} · {lesson.product_name}
          </span>
          <span className="detail">{[`${coming.length} athlete${coming.length === 1 ? '' : 's'}`, lesson.facility_name].join(' · ')}</span>
        </span>
        {outcome ? <span className={`outcome ${outcomeClass}`}>{outcome.label}</span> : null}
        <span className="chevron" aria-hidden="true">
          ›
        </span>
      </Link>
    );
  }

  return (
    <Link className="hour-row" to={`/coach/lessons/${lesson.lesson_id}?on=${on}`} state={{ list: `${pathname}${search}` }}>
      <span className="hour-row-text">
        <span className="name">
          {dayAndTime(lesson.slot_start)} · {who || 'Nobody named'}
        </span>
        <span className="detail">
          {[lesson.product_name, lesson.resource_name, lesson.facility_name].filter(Boolean).join(' · ')}
        </span>
        {cancelled.map((each) => (
          <span key={each.lesson_id} className="sub-line">
            {cancelledLine(each, coming.length === 0)}
          </span>
        ))}
      </span>
      {outcome ? <span className={`outcome ${outcomeClass}`}>{outcome.label}</span> : null}
      {placesOnSale ? <span className="places">{free === 1 ? '1 place' : `${free} places`}</span> : null}
      <span className="chevron" aria-hidden="true">
        ›
      </span>
    </Link>
  );
}

/**
 * "Cancelled by Jenna Lee · Tue 13 Oct" when the whole hour is off, or
 * "Leo Grant cancelled · Wed 14 Oct" when others are still coming.
 */
function cancelledLine(lesson: CoachLesson, wholeHour: boolean): string {
  const cancellation = lesson.cancellation;
  if (!cancellation) return '';
  const by = cancellation.by === 'coach' ? 'you' : (cancellation.byName ?? 'the family');
  const on = shortDay(cancellation.at);
  return wholeHour
    ? `Cancelled by ${by} · ${on}`
    : `${lesson.athlete.name} cancelled${cancellation.by === 'coach' ? ' by you' : ''} · ${on}`;
}

function shortDay(iso: string): string {
  return new Date(iso).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
}

/** "Fri 16 Oct, 18:00" */
/** A clinic with nobody coming (31 October): the same row as one with athletes. */
export function EmptyClinicRow({ clinic }: { clinic: EmptyClinic }) {
  return (
    <Link className="hour-row" to={`/coach/clinics/${clinic.clinic_id}`}>
      <span className="hour-row-text">
        <span className="name">
          {dayAndTime(clinic.slot_start)} · {clinic.title}
        </span>
        <span className="detail">{`0 athletes · ${clinic.facility_name}`}</span>
      </span>
      <span className="chevron" aria-hidden="true">
        ›
      </span>
    </Link>
  );
}

function dayAndTime(iso: string): string {
  return `${shortDay(iso)}, ${new Date(iso).toLocaleTimeString('en-GB', { hour: 'numeric', minute: '2-digit' })}`;
}

/** YYYY-MM-DD in the viewer's own clock. */
export function isoDay(day: Date): string {
  const month = `${day.getMonth() + 1}`.padStart(2, '0');
  const date = `${day.getDate()}`.padStart(2, '0');
  return `${day.getFullYear()}-${month}-${date}`;
}
